import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollTriggerProps } from "../types";
gsap.registerPlugin(ScrollTrigger);

const SELECTORS = {
  track: ".parallax-gallery__track",
  item: ".parallax-gallery__item",
  parallaxSlides: '.scrollslide[data-animation-type="parallax"]',
  slide: ".scrollslide",
} as const;

type ParallaxPreset = {
  parallaxSpan: number;
  startOffsetBase: number;
  startOffsetStep: number;
  distanceBase: number;
  distanceStep: number;
  speedStep: number;
  overlapStep: number;
  delayStep: number;
};

const PRESETS: Record<"soft" | "medium" | "strong", ParallaxPreset> = {
  soft: {
    parallaxSpan: 0.78,
    startOffsetBase: 130,
    startOffsetStep: 48,
    distanceBase: 110,
    distanceStep: 36,
    speedStep: 0.09,
    overlapStep: 7,
    delayStep: 0.05,
  },
  medium: {
    parallaxSpan: 0.9,
    startOffsetBase: 150,
    startOffsetStep: 58,
    distanceBase: 140,
    distanceStep: 45,
    speedStep: 0.1,
    overlapStep: 9,
    delayStep: 0.055,
  },
  strong: {
    parallaxSpan: 1.05,
    startOffsetBase: 170,
    startOffsetStep: 70,
    distanceBase: 170,
    distanceStep: 55,
    speedStep: 0.12,
    overlapStep: 10,
    delayStep: 0.06,
  },
};

const PARALLAX_INTENSITY: keyof typeof PRESETS = "soft";
const cfg = PRESETS[PARALLAX_INTENSITY];

const exitStart = 0.64;
const exitDistanceFactor = 0.65;
const enterWindow = 0.38;
const easeOut = gsap.parseEase("power2.out");

export const ParallaxScrollTrigger = ({
  timeline,
  section,
  step,
}: ScrollTriggerProps) => {
  const track = section.querySelector<HTMLElement>(SELECTORS.track);
  const items = track?.querySelectorAll<HTMLElement>(SELECTORS.item);
  if (!track || !items) throw new Error("Track or items not found");

  const itemsArray: HTMLElement[] = Array.from(items);
  const sectionYSetter = gsap.quickSetter(section, "y", "px") as (
    value: number
  ) => void;
  const itemYSetters: Array<(value: number) => void> = itemsArray.map(
    (item, i) => {
      gsap.set(item, {
        y: 0,
        zIndex: itemsArray.length - i,
        transformOrigin: "center center",
        force3D: true,
      });
      return gsap.quickSetter(item, "y", "px") as (value: number) => void;
    }
  );

  const itemMotion: Array<{
    startOffset: number;
    distance: number;
    speed: number;
    overlap: number;
    delay: number;
  }> = itemsArray.map((_, i) => {
    const depth = i + 1;
    return {
      startOffset: cfg.startOffsetBase + i * cfg.startOffsetStep,
      distance: cfg.distanceBase + depth * cfg.distanceStep,
      speed: 1 + depth * cfg.speedStep,
      overlap: i === 0 ? 0 : depth * cfg.overlapStep,
      delay: i * cfg.delayStep,
    };
  });

  gsap.set(section, {
    y: 0,
    opacity: 1,
    force3D: true,
  });

  const progressProxy = { value: 0 };
  const at = step;

  timeline.to(
    progressProxy,
    {
      value: 1,
      duration: cfg.parallaxSpan,
      ease: "none",
      onUpdate: () => {
        const maxProgress = gsap.utils.clamp(0, 1, progressProxy.value);
        const exitProgress =
          maxProgress <= exitStart
            ? 0
            : (maxProgress - exitStart) / (1 - exitStart);
        const clampedExitProgress = gsap.utils.clamp(0, 1, exitProgress);
        const enterProgress = gsap.utils.clamp(0, 1, maxProgress / enterWindow);
        const easedEnterProgress = easeOut(enterProgress);

        sectionYSetter(
          -clampedExitProgress * window.innerHeight * exitDistanceFactor
        );

        itemYSetters.forEach((setY, i) => {
          const motion = itemMotion[i];
          if (!motion) return;
          const { startOffset, distance, speed, overlap, delay } = motion;
          const localProgress = gsap.utils.clamp(
            0,
            1,
            (easedEnterProgress - delay) / (1 - delay)
          );
          const y =
            startOffset -
            localProgress * distance * speed -
            maxProgress * overlap;
          setY(y);
        });
      },
    },
    at
  );
};
